import React, { useEffect, useState } from "react";
import { 
    Box, 
    Typography, 
    useTheme, 
    useMediaQuery,
    AppBar,
    Toolbar,
    IconButton,
    Menu,
    MenuItem,
    Skeleton
} from "@mui/material";
import Logo from "../../assests/logo.png";
import MenuIcon from "@mui/icons-material/Menu";
import { Link, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { getUserFrontendUrl, getFrontendUrl, getStorageUrl } from "../../config/config";
import { getMenus, getServiceSettings } from "../../api/api";

const Thankyou = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuItems, setMenuItems] = useState([]);
    const [logoLoaded, setLogoLoaded] = useState(false);
    const [thankYouMsg, setThankYouMsg] = useState(null);
    const [thankYouDesc, setThankYouDesc] = useState(null);
    const location = useLocation();
    const open = Boolean(anchorEl);

    // Get ticket details from location state first (since cookie may be cleared), then cookie
    const ticketDataFromState = location.state?.ticketData;
    const ticketDetailsFromCookie = Cookies.get("ticketDetails");
    const ticketData = ticketDataFromState || (ticketDetailsFromCookie ? JSON.parse(ticketDetailsFromCookie) : null);
    const branchId = ticketData?.client?.id; // Extract branch ID from ticket data

    // Get logo filename from ticketData — if none uploaded, fall back to default logo
    const logoFilename = ticketData?.client?.logo;
    const logoUrl = logoFilename
        ? getStorageUrl(`/storage/logos/${logoFilename}`)
        : Logo;

    // Preload logo for faster loading (only for remote URLs, not local imports)
    useEffect(() => {
        if (logoUrl && logoFilename && logoUrl.startsWith('http')) {
            try {
                // Create link element for preloading
                const link = document.createElement('link');
                link.rel = 'preload';
                link.as = 'image';
                link.href = logoUrl;
                link.fetchPriority = 'high';
                document.head.appendChild(link);

                // Also preconnect to the domain for faster connection
                const preconnect = document.createElement('link');
                preconnect.rel = 'preconnect';
                preconnect.href = new URL(logoUrl).origin;
                document.head.appendChild(preconnect);

                return () => {
                    // Cleanup on unmount
                    if (document.head.contains(link)) {
                        document.head.removeChild(link);
                    }
                    if (document.head.contains(preconnect)) {
                        document.head.removeChild(preconnect);
                    }
                };
            } catch (error) {
                console.warn('Error preloading logo:', error);
            }
        }
    }, [logoUrl, logoFilename]);

    // Reset logo loaded state when logoUrl changes and add timeout fallback
    useEffect(() => {
        setLogoLoaded(false);
        
        // Fallback: if logo doesn't load within 5 seconds, show it anyway
        const timeout = setTimeout(() => {
            console.warn('Logo loading timeout, showing image anyway');
            setLogoLoaded(true);
        }, 5000);
        
        return () => clearTimeout(timeout);
    }, [logoUrl]);

    // Fix scrolling issue by overriding global overflow hidden and scroll to top
    useEffect(() => {
        const rootElement = document.getElementById('root');
        const htmlElement = document.documentElement;
        const bodyElement = document.body;
        
        // Save original styles
        const originalBodyOverflow = bodyElement.style.overflow;
        const originalBodyHeight = bodyElement.style.height;
        const originalHtmlOverflow = htmlElement.style.overflow;
        const originalHtmlHeight = htmlElement.style.height;
        const originalRootOverflow = rootElement?.style.overflow;
        const originalRootHeight = rootElement?.style.height;
        
        // Enable scrolling
        bodyElement.style.overflow = 'auto';
        bodyElement.style.height = 'auto';
        htmlElement.style.overflow = 'auto';
        htmlElement.style.height = 'auto';
        if (rootElement) {
            rootElement.style.overflow = 'auto';
            rootElement.style.height = 'auto';
        }
        
        // Add CSS class
        bodyElement.classList.add('scrollable-page');
        htmlElement.classList.add('scrollable-page');
        
        // Scroll to top immediately when component mounts
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        if (rootElement) {
            rootElement.scrollTop = 0;
        }
        
        // Also scroll to top after a small delay to ensure it works
        const scrollTimeout = setTimeout(() => {
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
            if (rootElement) {
                rootElement.scrollTop = 0;
            }
        }, 100);
        
        return () => {
            clearTimeout(scrollTimeout);
            // Restore original styles
            bodyElement.style.overflow = originalBodyOverflow;
            bodyElement.style.height = originalBodyHeight;
            htmlElement.style.overflow = originalHtmlOverflow;
            htmlElement.style.height = originalHtmlHeight;
            if (rootElement) {
                rootElement.style.overflow = originalRootOverflow;
                rootElement.style.height = originalRootHeight;
            }
            bodyElement.classList.remove('scrollable-page');
            htmlElement.classList.remove('scrollable-page');
        };
    }, []);

    // Fetch fresh service settings (thank you text) from API
    useEffect(() => {
        const fetchSettings = async () => {
            if (!branchId) return;
            try {
                const response = await getServiceSettings(branchId);
                if (response.success && response.data) {
                    if (response.data.thank_you_message) {
                        setThankYouMsg(response.data.thank_you_message);
                    }
                    if (response.data.thank_you_description) {
                        setThankYouDesc(response.data.thank_you_description);
                    }
                }
            } catch (err) {
                console.error("Error fetching service settings for Thankyou:", err);
            }
        };
        fetchSettings();
    }, [branchId]);

    // Fetch menu items when branchId is available
    useEffect(() => {
        const fetchMenuItems = async () => {
            if (!branchId) {
                return;
            }

            try {
                const response = await getMenus(branchId);
                if (response.success) {
                    setMenuItems(response.data || []);
                }
            } catch (error) {
                console.error("Error fetching menu items:", error);
                // Set empty array on error to prevent issues
                setMenuItems([]);
            }
        };

        fetchMenuItems();
    }, [branchId]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <Box sx={{ flexGrow: 1 }}>
                <AppBar position="static">
                    <Toolbar>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                mr: 2,
                            }}
                        >
                            <img
                                src={Logo}
                                alt="Logo"
                                style={{
                                    height: "24px",
                                    marginRight: "8px",
                                    filter: "brightness(0) invert(1)", // Make logo white
                                }}
                            />
                        </Box>
                        <Typography
                            variant="h6"
                            component="div"
                            sx={{ flexGrow: 1 }}
                        >
                        </Typography>

                        <IconButton
                            size="large"
                            color="inherit"
                            aria-label="menu"
                            sx={{ ml: 2 }}
                            onClick={handleClick}
                        >
                            <MenuIcon />
                        </IconButton>

                        {/* Top menu */}
                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                        >
                            {/* Default HOME menu item */}
                            <MenuItem onClick={handleClose}>
                                <a
                                    href={getUserFrontendUrl(`/get-ticket/${ticketData?.ticket_code || "DEMO"}`)}
                                    rel="noopener noreferrer"
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                >
                                    HOME
                                </a>
                            </MenuItem>
                            {/* Dynamic menu items from database - only show if admin has set a real URL (not placeholder) */}
                            {menuItems
                                .filter((menu) => {
                                    if (!menu.menu_link || (!menu.menu_link.startsWith('http://') && !menu.menu_link.startsWith('https://'))) return false;
                                    const placeholders = [
                                        'https://www.facebook.com/yourpage',
                                        'https://www.instagram.com/yourprofile',
                                        'https://www.example.com/contact',
                                    ];
                                    return !placeholders.includes(menu.menu_link.trim().toLowerCase());
                                })
                                .map((menu) => (
                                    <MenuItem key={menu.id} onClick={() => {
                                        handleClose();
                                        window.open(menu.menu_link, '_blank', 'noopener,noreferrer');
                                    }}>
                                        {menu.menu_title}
                                    </MenuItem>
                                ))}
                        </Menu>
                    </Toolbar>
                </AppBar>
            </Box>

            <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                minHeight: "100vh",
                maxWidth: { xs: "100%", sm: 450 },
                mx: "auto",
                textAlign: "center",
                px: { xs: 2, sm: 3 },
                py: { xs: 3, sm: 4 },
                pt: { xs: 2, sm: 3 },
                mb: 4,
                boxSizing: "border-box",
                overflowY: "auto",
                overflowX: "hidden"
            }}
        >
            <Typography variant="h5" fontWeight="bold" mb={{ xs: 2, sm: 3 }} sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: isMobile ? 60 : 120 }}>
                {!logoLoaded && (
                    <Skeleton 
                        variant="rectangular" 
                        width={isMobile ? "70%" : 300} 
                        height={isMobile ? 60 : 120} 
                        sx={{ borderRadius: 1, maxWidth: "100%", position: "absolute" }}
                    />
                )}
                <img 
                    src={logoUrl} 
                    alt="FMB Logo"
                    loading="eager"
                    fetchPriority="high"
                    style={{
                        width: isMobile ? "70%" : 300,
                        maxWidth: "100%",
                        height: "auto",
                        opacity: logoLoaded ? 1 : 0,
                        transition: "opacity 0.3s ease-in-out",
                        margin: "0 auto",
                        position: "relative"
                    }}
                    onLoad={() => {
                        console.log('Logo loaded successfully');
                        setLogoLoaded(true);
                    }}
                    onError={(e) => {
                        console.error('Logo failed to load:', logoUrl, e);
                        // Show fallback logo if error
                        if (logoUrl !== Logo) {
                            e.target.src = Logo;
                        }
                        setLogoLoaded(true);
                    }}
                />
            </Typography>
            <Typography 
                variant="h4" 
                mt={{ xs: 2, sm: 3 }} 
                mb={{ xs: 3, sm: 4 }}
                fontWeight="bold"
                sx={{
                    fontSize: { xs: "1.5rem", sm: "2.125rem" },
                    px: { xs: 2, sm: 0 },
                    lineHeight: { xs: 1.4, sm: 1.5 }
                }}
            >
                {thankYouMsg || ticketData?.client?.thank_you_message || "Thank you for your visit and welcome back!"}
            </Typography>

            <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                    mb: { xs: 3, sm: 4 },
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    px: { xs: 2, sm: 0 },
                    lineHeight: { xs: 1.5, sm: 1.6 }
                }}
            >
                {thankYouDesc || ticketData?.client?.thank_you_description || "We hope you received good help and are satisfied with our service. Welcome back!"}
            </Typography>

            <Box sx={{ mt: { xs: 4, sm: 6 }, mb: 2 }}>
                <Typography
                    variant="body1"
                    color="primary"
                    fontWeight="bold"
                    mt={3}
                    sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                >
                    <Link style={{ textDecoration: "none", color: "inherit" }}>easyQue</Link> (c) by Digifront
                </Typography>
                <Typography
                    variant="body2"
                    color="primary"
                    component="a"
                    href="mailto:sales@digifront.biz"
                    sx={{ 
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        textDecoration: "none"
                    }}
                >
                    sales@digifront.biz
                </Typography>
            </Box>
        </Box>
        </>
    );
};

export default Thankyou;
